<template>
  <div class="detail-content">
    <el-descriptions :column="column" :border="border" :size="size">
      <el-descriptions-item
        v-for="(col, index) in filteredColumns"
        :key="index"
        :label="col.label"
        :span="col.span || 1"
        :label-style="col.labelStyle"
        :content-style="col.contentStyle"
        :label-class-name="col.labelClassName"
        :content-class-name="col.contentClassName"
      >
        <!-- 图片类型 -->
        <template v-if="col.type === 'image'">
          <div v-if="getValue(col.prop)" class="image-container">
            <el-image
              :src="getValue(col.prop)"
              :preview-src-list="[getValue(col.prop)]"
              fit="cover"
              style="width: 100px; height: 100px;"
            >
              <div slot="error" class="image-slot">
                <i class="el-icon-picture-outline"></i>
              </div>
            </el-image>
          </div>
          <span v-else>-</span>
        </template>

        <!-- 多图片类型 -->
        <template v-else-if="col.type === 'images'">
          <div v-if="getImageArray(col.prop).length > 0" class="images-container">
            <el-image
              v-for="(img, idx) in getImageArray(col.prop)"
              :key="idx"
              :src="img"
              :preview-src-list="getImageArray(col.prop)"
              fit="cover"
              style="width: 60px; height: 60px; margin-right: 8px; margin-bottom: 8px;"
            >
              <div slot="error" class="image-slot">
                <i class="el-icon-picture-outline"></i>
              </div>
            </el-image>
          </div>
          <span v-else>-</span>
        </template>

        <!-- 数组类型 -->
        <template v-else-if="col.type === 'array'">
          <div v-if="getArrayValue(col.prop).length > 0">
            <el-tag
              v-for="(item, idx) in getArrayValue(col.prop)"
              :key="idx"
              size="mini"
              :type="col.tagType || 'primary'"
              style="margin-right: 5px; margin-bottom: 5px;"
            >
              {{ getArrayItemText(item, col.arrayKey) }}
            </el-tag>
          </div>
          <span v-else>-</span>
        </template>

        <!-- 逗号分隔字符串 -->
        <template v-else-if="col.type === 'comma'">
          <div v-if="getValue(col.prop)">
            <el-tag
              v-for="(item, idx) in getValue(col.prop).split(',')"
              :key="idx"
              size="mini"
              :type="col.tagType || 'primary'"
              style="margin-right: 5px; margin-bottom: 5px;"
            >
              {{ item.trim() }}
            </el-tag>
          </div>
          <span v-else>-</span>
        </template>

        <!-- 日期类型 -->
        <template v-else-if="col.type === 'date'">
          <span>{{ formatDate(getValue(col.prop), col.format) }}</span>
        </template>

        <!-- 标签类型 -->
        <template v-else-if="col.type === 'tag'">
          <el-tag
            :type="getTagType(getValue(col.prop), col.tagMap)"
            size="mini"
          >
            {{ getTagText(getValue(col.prop), col.tagMap) }}
          </el-tag>
        </template>

        <!-- 链接类型 -->
        <template v-else-if="col.type === 'link'">
          <a
            v-if="getValue(col.prop)"
            :href="getValue(col.prop)"
            target="_blank"
            class="detail-link"
            :class="`detail-link--${col.linkType || 'primary'}`"
          >
            {{ col.linkText || getValue(col.prop) }}
          </a>
          <span v-else>-</span>
        </template>

        <!-- 文件下载类型 -->
        <template v-else-if="col.type === 'file'">
          <div v-if="getValue(col.prop)" class="file-download">
            <a
              :href="getValue(col.prop)"
              :download="getFileNameFromUrl(getValue(col.prop))"
              class="detail-link detail-link--file"
              target="_blank"
            >
              <i class="el-icon-download"></i>
              {{ getFileNameFromUrl(getValue(col.prop)) }}
            </a>
          </div>
          <span v-else>-</span>
        </template>

        <!-- 视频播放类型 -->
        <template v-else-if="col.type === 'video'">
          <div v-if="getValue(col.prop)" class="video-player">
            <video
              :src="getValue(col.prop)"
              controls
              preload="metadata"
              style="width: 100%; max-width: 400px; height: 225px;"
            >
              您的浏览器不支持视频播放
            </video>
            <div class="video-info">
              <span class="video-name">{{ getFileNameFromUrl(getValue(col.prop)) }}</span>
            </div>
          </div>
          <span v-else>-</span>
        </template>

        <!-- HTML类型 -->
        <template v-else-if="col.type === 'html'">
          <div v-if="getValue(col.prop)" class="html-content" v-html="getValue(col.prop)"></div>
          <span v-else>-</span>
        </template>

        <!-- 自定义渲染 -->
        <template v-else-if="col.type === 'custom'">
          <slot :name="col.prop" :data="data" :value="getValue(col.prop)">
            {{ getValue(col.prop)===undefined || getValue(col.prop)===null || getValue(col.prop)==='' ? '-' : getValue(col.prop) }}
          </slot>
        </template>

        <!-- 默认文本类型 -->
        <template v-else>
          <span>{{ formatValue(col.prop, col.format) }}</span>
        </template>
      </el-descriptions-item>
    </el-descriptions>

    <!-- 额外插槽 -->
    <slot name="extra" :data="data"></slot>
  </div>
</template>

<script>
import { formatDate } from "@/utils/filters/filter";

export default {
  name: 'DetailContent',
  props: {
    // 数据源
    data: {
      type: Object,
      default: () => ({})
    },
    // 列配置
    columns: {
      type: Array,
      default: () => []
    },
    // 每行显示的列数
    column: {
      type: Number,
      default: 2
    },
    // 是否显示边框
    border: {
      type: Boolean,
      default: true
    },
    // 尺寸
    size: {
      type: String,
      default: 'medium'
    }
  },
  methods: {
    // 获取值
    getValue(prop) {
      if (!prop || !this.data) return '';
      
      // 支持嵌套属性 如: user.name
      const keys = prop.split('.');
      let value = this.data;
      
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          return '';
        }
      }
      
      return value;
    },

    // 获取数组值
    getArrayValue(prop) {
      const value = this.getValue(prop);
      if (Array.isArray(value)) {
        return value;
      }
      return [];
    },

    // 获取数组项文本
    getArrayItemText(item, key) {
      if (key && typeof item === 'object') {
        return item[key] || item;
      }
      return item;
    },

    // 获取图片数组
    getImageArray(prop) {
      const value = this.getValue(prop);
      if (Array.isArray(value)) {
        return value;
      } else if (typeof value === 'string' && value) {
        // 如果是逗号分隔的字符串
        return value.split(',').map(url => url.trim()).filter(url => url);
      }
      return [];
    },

    // 格式化日期
    formatDate(value, format = 'YYYY-MM-DD HH:mm:ss') {
      if (!value) return '-';
      return formatDate(value, format);
    },

    // 获取标签类型
    getTagType(value, tagMap) {
      if (tagMap && tagMap[value]) {
        return tagMap[value].type || 'primary';
      }
      return 'primary';
    },

    // 获取标签文本
    getTagText(value, tagMap) {
      if (tagMap && tagMap[value]) {
        return tagMap[value].text || value;
      }
      return value;
    },

    // 从URL中获取文件名
    getFileNameFromUrl(url) {
      if (!url) return '';
      const parts = url.split('/');
      const fileName = parts[parts.length - 1];
      return fileName || '下载文件';
    },

    // 格式化值
    formatValue(prop, format) {
      const value = this.getValue(prop);
      
      // 如果值为空，返回 '-'
      if (value === undefined || value === null || value === '') {
        return '-';
      }
      
      // 如果有格式化函数，调用它
      if (format && typeof format === 'function') {
        try {
          return format.call(this, value);
        } catch (error) {
          console.error('格式化函数执行失败:', error);
          return value;
        }
      }
      
      return value;
    }
  },
  computed: {
    filteredColumns() {
      return this.columns.filter(col => !col.hide);
    }
  }
};
</script>

<style lang="scss" scoped>
.detail-content {
  .image-container {
    display: inline-block;
  }

  .images-container {
    display: flex;
    flex-wrap: wrap;
  }

  .image-slot {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: #f5f7fa;
    color: #909399;
    font-size: 14px;
  }
  
  .detail-link {
    color: #409EFF;
    text-decoration: none;
    cursor: pointer;
    
    &:hover {
      color: #66b1ff;
    }
    
    &--primary {
      color: #409EFF;
    }
    
    &--success {
      color: #67C23A;
    }
    
    &--warning {
      color: #E6A23C;
    }
    
    &--danger {
      color: #F56C6C;
    }
    
    &--info {
      color: #909399;
    }

    &--file {
      color: #67C23A;
      display: inline-flex;
      align-items: center;
      padding: 4px 8px;
      border: 1px solid #67C23A;
      border-radius: 4px;
      transition: all 0.3s;

      &:hover {
        background-color: #67C23A;
        color: white;
      }

      i {
        margin-right: 4px;
      }
    }
  }

  .file-download {
    display: inline-block;
  }

  .video-player {
    display: inline-block;
    border: 1px solid #EBEEF5;
    border-radius: 4px;
    overflow: hidden;

    video {
      display: block;
      background: #000;
    }

    .video-info {
      padding: 8px 12px;
      background: #F5F7FA;
      border-top: 1px solid #EBEEF5;

      .video-name {
        font-size: 12px;
        color: #606266;
      }
    }
  }

  .html-content {
    max-height: 400px;
    overflow-y: auto;
    padding: 12px;
    border: 1px solid #EBEEF5;
    border-radius: 4px;
    background-color: #FAFAFA;
    line-height: 1.6;
    font-size: 14px;
    
    // 重置内部HTML样式
    ::v-deep {
      p {
        margin: 0 0 10px 0;
        &:last-child {
          margin-bottom: 0;
        }
      }
      
      h1, h2, h3, h4, h5, h6 {
        margin: 16px 0 8px 0;
        &:first-child {
          margin-top: 0;
        }
      }
      
      img {
        max-width: 100%;
        height: auto;
        display: block;
        margin: 10px 0;
      }
      
      ul, ol {
        margin: 10px 0;
        padding-left: 20px;
      }
      
      blockquote {
        margin: 10px 0;
        padding: 10px 15px;
        background-color: #f4f4f5;
        border-left: 4px solid #409EFF;
      }
      
      code {
        background-color: #f4f4f5;
        padding: 2px 4px;
        border-radius: 3px;
        font-family: 'Courier New', monospace;
      }
      
      pre {
        background-color: #f4f4f5;
        padding: 12px;
        border-radius: 4px;
        overflow-x: auto;
        
        code {
          background-color: transparent;
          padding: 0;
        }
      }
      
      table {
        border-collapse: collapse;
        width: 100%;
        margin: 10px 0;
        
        th, td {
          border: 1px solid #EBEEF5;
          padding: 8px 12px;
          text-align: left;
        }
        
        th {
          background-color: #F2F6FC;
          font-weight: bold;
        }
      }
      
      a {
        color: #409EFF;
        text-decoration: none;
        
        &:hover {
          color: #66b1ff;
          text-decoration: underline;
        }
      }
    }
  }
}
</style> 